import React, { useState, useEffect } from 'react'
import imageUrlBuilder from '@sanity/image-url'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import useMediaQuery from '@mui/material/useMediaQuery'
import _ from 'lodash'
// @ts-expect-error -- @sanity/client@0.144.0 does not provide TypeScript declarations
import sanityClient from '@sanity/client'

import './care-team-grid.css'
import { CareTeamProps } from './CareTeam'
import { getVisibleSpecialties } from './utils'

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
  useCdn: false,
})

const CareTeamGrid: React.FC<{ data: CareTeamProps }> = ({ data }) => {
  const {
    showSpecialtyFilter,
    showProviderTypeFilter = true,
    showLocationFilter = true,
    backgroundColor,
    spacing = 'pyl',
  } = data
  const itemsPerRow = data.itemsPerRow ?? 3
  const mobileItemsPerRow = data.mobileItemsPerRow ?? 1
  const isMobileDevice = useMediaQuery('(max-width: 768px)')
  const isTabletDevice = useMediaQuery(
    '(min-width: 769px) and (max-width: 1099px)'
  )
  const builder = imageUrlBuilder(client)

  const [allProviderTypes, setAllProviderTypes] = useState<string[]>(['all'])
  const [allLocations, setAllLocations] = useState<string[]>(['all'])
  const [allSpecialties, setAllSpecialties] = useState<string[]>(['all'])
  const [selectedFilters, setSelectedFilters] = useState({
    specialties: 'all',
    providerType: 'all',
    location: 'all',
  })
  const [filteredProviders, setFilteredProviders] = useState(
    data?.providers || []
  )
  const desktopVisibleCount = data?.visibleCount || itemsPerRow * 2
  const initialVisibleCount = isMobileDevice
    ? data?.mobileVisibleCount || desktopVisibleCount
    : desktopVisibleCount
  let effectiveItemsPerRow = itemsPerRow
  if (isTabletDevice) effectiveItemsPerRow = 2
  if (isMobileDevice) effectiveItemsPerRow = mobileItemsPerRow
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount)

  useEffect(() => {
    setVisibleCount(initialVisibleCount)
  }, [initialVisibleCount])

  const getImageUrl = (asset: any) => {
    if (!asset) return ''
    return builder.image(asset).url()
  }

  // this add the unique values to the provider types, locations and specialties
  useEffect(() => {
    const uniqueSpecialties = new Set<string>()
    const uniqueProviderTypes = new Set<string>()
    const uniqueLocations = new Set<string>()

    data?.providers?.forEach((provider: any) => {
      provider?.content?.main?.specialties?.forEach((specialty: string) => {
        uniqueSpecialties.add(specialty?.toLocaleLowerCase())
      })
      if (provider?.content?.main?.team) {
        uniqueProviderTypes.add(provider.content.main.team.toLocaleLowerCase())
      }
      provider?.content?.main?.locations?.forEach((location: any) => {
        if (location?.content?.main?.title) {
          uniqueLocations.add(location.content.main.title.toLocaleLowerCase())
        }
      })
    })

    setAllProviderTypes(['all', ...Array.from(uniqueProviderTypes)])
    setAllLocations(['all', ...Array.from(uniqueLocations)])
    setAllSpecialties(['all', ...Array.from(uniqueSpecialties)])
  }, [data?.providers])

  // this filter the providers based on the selected filters
  useEffect(() => {
    const filtered = data?.providers?.filter((provider: any) => {
      const matchesSpecialty =
        selectedFilters.specialties === 'all' ||
        provider?.content?.main?.specialties?.some(
          (specialty: string) =>
            selectedFilters.specialties === specialty?.toLocaleLowerCase()
        )

      const matchesProviderType =
        selectedFilters.providerType === 'all' ||
        provider?.content?.main?.team?.toLocaleLowerCase() ===
          selectedFilters.providerType

      const matchesLocation =
        selectedFilters.location === 'all' ||
        provider?.content?.main?.locations?.some(
          (location: any) =>
            location?.content?.main?.title?.toLocaleLowerCase() ===
            selectedFilters.location
        )

      return matchesSpecialty && matchesProviderType && matchesLocation
    })

    setFilteredProviders(filtered || [])
  }, [selectedFilters, data?.providers])

  const handleFilterChange = (filterType: string, value: string) => {
    setVisibleCount(initialVisibleCount)
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value,
    }))
  }

  const handleShowMore = () => {
    setVisibleCount(prev =>
      Math.min(prev + effectiveItemsPerRow, filteredProviders.length)
    )
  }

  const handleShowLess = () => {
    setVisibleCount(initialVisibleCount)
    const firstCard = document.querySelector('.care-team-grid__card_item')
    if (firstCard) {
      firstCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const visibleProviders = filteredProviders.slice(0, visibleCount)
  const nominalColumnCount = Math.max(1, itemsPerRow)
  const cardWidth = `calc((100% - ${
    nominalColumnCount - 1
  }rem) / ${nominalColumnCount})`
  const showFilters =
    showProviderTypeFilter || showLocationFilter || showSpecialtyFilter

  return (
    <section
      id={data.options?.uid || data.deepLinkId}
      className={`care-team-grid__background care-team-grid__background--mobile-columns-${mobileItemsPerRow} ${
        backgroundColor || ''
      } ${spacing}`}
    >
      <div className="care-team-grid__container">
        {(data?.title || data?.subtitle) && (
          <div className="care-team__heading">
            {data?.title && (
              <h2 className="care-team-grid__title">{data.title}</h2>
            )}
            {data?.subtitle && (
              <p className="care-team-grid__subtitle">{data.subtitle}</p>
            )}
          </div>
        )}

        {showFilters && (
          <div className="care-team-grid__filter_container">
            {showProviderTypeFilter && (
              <FormControl className="care-team-grid__filter">
                <InputLabel
                  id="care-team-grid__provider_type_filter__label"
                  htmlFor="care-team-grid__provider_type_filter"
                >
                  Provider Type
                </InputLabel>
                <Select
                  labelId="care-team-grid__provider_type_filter__label"
                  id="care-team-grid__provider_type_filter"
                  value={selectedFilters.providerType}
                  onChange={e =>
                    handleFilterChange('providerType', e.target.value)
                  }
                  variant="outlined"
                >
                  {allProviderTypes?.map((type, index) => (
                    <MenuItem key={index} value={type}>
                      {type === 'all' ? 'All' : _.startCase(_.toLower(type))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {showLocationFilter && (
              <FormControl className="care-team-grid__filter">
                <InputLabel
                  id="care-team-grid__location_filter__label"
                  htmlFor="care-team-grid__location_filter"
                >
                  Location
                </InputLabel>
                <Select
                  labelId="care-team-grid__location_filter__label"
                  id="care-team-grid__location_filter"
                  value={selectedFilters.location}
                  onChange={e => handleFilterChange('location', e.target.value)}
                  variant="outlined"
                >
                  {allLocations?.map((location, index) => (
                    <MenuItem key={index} value={location}>
                      {location === 'all'
                        ? 'All'
                        : _.startCase(_.toLower(location))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {showSpecialtyFilter && (
              <FormControl className="care-team-grid__filter">
                <InputLabel
                  id="care-team-grid__specialties_filter__label"
                  htmlFor="care-team-grid__specialties_filter"
                >
                  Specialties
                </InputLabel>
                <Select
                  labelId="care-team-grid__specialties_filter__label"
                  id="care-team-grid__specialties_filter"
                  value={selectedFilters.specialties}
                  onChange={e =>
                    handleFilterChange('specialties', e.target.value)
                  }
                  variant="outlined"
                >
                  {allSpecialties?.map((specialty, index) => (
                    <MenuItem key={index} value={specialty}>
                      {specialty === 'all'
                        ? 'All'
                        : _.startCase(_.toLower(specialty))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        )}

        <div className="care-team-grid__cards_container">
          {visibleProviders?.map((provider: any) => {
            const main = provider?.content?.main
            const slug = main?.slug?.current
            const providerURL = `/team/${slug}/`
            const imageUrl = getImageUrl(main?.image?.asset)

            const { visible, remainingCount } = getVisibleSpecialties(
              main?.specialties || [],
              isMobileDevice && mobileItemsPerRow === 2 ? 2 : 5
            )
            return (
              <div
                className="care-team-grid__card_item"
                key={provider?._key}
                style={{ flexBasis: cardWidth }}
              >
                <div className="care-team-grid__provider_details">
                  <div className="care-team-grid__provider_image_container">
                    <img
                      className="care-team-grid__provider_image"
                      src={imageUrl || ''}
                      alt={main?.title || 'Care team provider'}
                    />
                  </div>
                  <div className="care-team-grid__provider_meta">
                    <div className="care-team-grid__provider_title">
                      {main?.title}
                    </div>
                    <div className="care-team-grid__specialties_container">
                      {visible?.map((specialty: any, index: number) => (
                        <div key={index} className="care-team-grid__specialty">
                          {specialty}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <div className="care-team-grid__specialty">
                          +{remainingCount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="care-team-grid__actions">
                  <a
                    className="care-team-grid__learn_more"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={providerURL}
                  >
                    View profile
                  </a>
                </div>
              </div>
            )
          })}
        </div>
        {filteredProviders.length === 0 ? (
          <div className="care-team-grid__empty" role="status">
            No providers match these filters. Try adjusting your selection.
          </div>
        ) : (
          <div className="care-team-grid__info_actions_container">
            <span>
              You’re viewing {Math.min(visibleCount, filteredProviders.length)}{' '}
              of {filteredProviders.length} providers
            </span>
            {visibleCount < filteredProviders.length ? (
              <button
                type="button"
                className="care-team-grid__show_more_button"
                onClick={handleShowMore}
              >
                Show More
              </button>
            ) : visibleCount > initialVisibleCount ? (
              <button
                type="button"
                className="care-team-grid__show_more_button"
                onClick={handleShowLess}
              >
                Show Less
              </button>
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}

export { CareTeamGrid }
export type { CareTeamProps }
